import * as React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { Finding, QuestionTrace, TraceCoverageRow, TracePassage } from "@hp/api-client";

type Tone = "zinc" | "emerald" | "rose" | "sky" | "amber";

export function Tag({ children, tone = "zinc" }: { children: React.ReactNode; tone?: Tone }) {
  const tones: Record<Tone, string> = {
    zinc: "bg-zinc-100 text-zinc-700",
    emerald: "bg-emerald-50 text-emerald-800",
    rose: "bg-rose-50 text-rose-800",
    sky: "bg-sky-50 text-sky-800",
    amber: "bg-amber-50 text-amber-800",
  };
  return <span className={["rounded px-1.5 py-0.5 text-xs font-medium", tones[tone]].join(" ")}>{children}</span>;
}

/** A fold: a heading with a count, closed by default, that opens to show its content. */
export function Fold({ title, count, children, open = false }: { title: string; count?: number | string; children: React.ReactNode; open?: boolean }) {
  const [shown, setShown] = React.useState(open);
  return (
    <div className="rounded-md border border-zinc-200 bg-white">
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-expanded={shown}
        className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
      >
        {shown ? <ChevronDown className="h-3.5 w-3.5" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
        {title}
        {count !== undefined ? <span className="ml-1 font-normal text-zinc-500">({count})</span> : null}
      </button>
      {shown ? <div className="border-t border-zinc-100 px-3 py-2">{children}</div> : null}
    </div>
  );
}

function NotRecorded() {
  return <p className="text-xs text-zinc-500">Not recorded for this question.</p>;
}

const SEVERITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2, info: 3 };
const SEVERITY_TONE: Record<string, Tone> = { high: "rose", medium: "amber", low: "sky", info: "zinc" };

export function severityTone(s: string): Tone {
  return SEVERITY_TONE[s] ?? "zinc";
}

/** The automatic checks: what could be missing from this answer. */
export function DiagnosisPanel({ findings }: { findings: Finding[] | undefined }) {
  if (!findings) return null;
  const sorted = [...findings].sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9));
  return (
    <section className="rounded-md border border-zinc-200 bg-white px-3 py-2" aria-label="What could be missing">
      <h3 className="text-xs font-semibold text-zinc-800">What could be missing</h3>
      {sorted.length === 0 ? (
        <p className="mt-1 text-xs text-zinc-600">No problems found by the automatic checks. This does not mean the answer is right.</p>
      ) : (
        <ul className="mt-1 space-y-2">
          {sorted.map((f, i) => (
            <li key={`${f.code}-${i}`} className="text-xs text-zinc-700">
              <div className="flex flex-wrap items-center gap-1.5">
                <Tag tone={severityTone(f.severity)}>{f.severity}</Tag>
                <span className="font-semibold text-zinc-900">{f.title}</span>
                <span className="font-mono text-[10px] text-zinc-400">{f.code}</span>
              </div>
              {f.detail ? <p className="mt-0.5">{f.detail}</p> : null}
              {f.hint ? <p className="mt-0.5 text-zinc-500">Hint: {f.hint}</p> : null}
              {f.evidence?.length ? (
                <ul className="mt-0.5 list-disc space-y-0.5 pl-5 text-zinc-500">
                  {f.evidence.map((e, j) => (
                    <li key={j}>{e}</li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Question label and scope chips. */
export function TraceHeader({ trace }: { trace: QuestionTrace }) {
  const { label, scope } = trace;
  if (!label && !scope) return null;
  const chips: React.ReactNode[] = [];
  if (label) {
    const tone = label.quality === "well-formed" ? "emerald" : label.quality === "vague" ? "rose" : "amber";
    chips.push(
      <Tag key="label" tone={tone}>
        {label.quality}
        {label.reason ? `: ${label.reason}` : ""}
      </Tag>,
    );
  }
  (scope?.contracts ?? []).forEach((c) => chips.push(<Tag key={`c-${c}`} tone="sky">contract {c}</Tag>));
  (scope?.states ?? []).forEach((c) => chips.push(<Tag key={`s-${c}`}>{c}</Tag>));
  (scope?.periods ?? []).forEach((c) => chips.push(<Tag key={`p-${c}`}>{c}</Tag>));
  if (scope?.as_of) chips.push(<Tag key="asof">as of {scope.as_of}</Tag>);
  if (scope?.clarify?.asked) chips.push(<Tag key="cl" tone="amber">clarification asked{scope.clarify.reason ? `: ${scope.clarify.reason}` : ""}</Tag>);
  if (scope?.borrowed_from_earlier) chips.push(<Tag key="bo" tone="amber">borrowed from an earlier question: {scope.borrowed_from_earlier}</Tag>);
  if (!chips.length) return null;
  return <div className="flex flex-wrap gap-1.5">{chips}</div>;
}

type SortKey = "score" | "none";

/** Every passage the writer saw, as a sortable table with a text preview on expand. */
export function ChunksTable({ items }: { items: TracePassage[] }) {
  const [sort, setSort] = React.useState<SortKey>("none");
  const [openRow, setOpenRow] = React.useState<number | null>(null);
  const rows = React.useMemo(() => {
    const indexed = items.map((p, i) => ({ p, i }));
    if (sort === "score") indexed.sort((a, b) => (b.p.score ?? 0) - (a.p.score ?? 0));
    return indexed;
  }, [items, sort]);
  if (!items.length) return <p className="text-xs text-zinc-500">None.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs text-zinc-700">
        <thead className="text-zinc-500">
          <tr>
            <th className="py-1 pr-2 font-medium">File</th>
            <th className="pr-2 font-medium">Page</th>
            <th className="pr-2 font-medium">Where</th>
            <th className="pr-2 font-medium">Layer</th>
            <th className="pr-2 font-medium">Kind</th>
            <th className="pr-2 font-medium">
              <button type="button" onClick={() => setSort((s) => (s === "score" ? "none" : "score"))} className="font-medium underline decoration-dotted">
                Score{sort === "score" ? " ▼" : ""}
              </button>
            </th>
            <th className="pr-2 font-medium">Why included</th>
            <th className="pr-2 font-medium">Chars</th>
            <th className="font-medium">Flags</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ p, i }) => (
            <React.Fragment key={`${p.key}-${i}`}>
              <tr className="cursor-pointer border-t border-zinc-100 align-top hover:bg-zinc-50" onClick={() => setOpenRow((v) => (v === i ? null : i))}>
                <td className="py-1 pr-2 font-medium text-zinc-900">{p.file}</td>
                <td className="pr-2">{p.page ?? ""}</td>
                <td className="pr-2">{p.section_path || p.where}</td>
                <td className="pr-2">{p.layer}</td>
                <td className="pr-2">{p.kind ?? ""}</td>
                <td className="pr-2">{p.score}</td>
                <td className="pr-2">{p.why ?? ""}</td>
                <td className="pr-2">{p.chars ?? ""}</td>
                <td className="space-x-1 whitespace-nowrap">
                  {p.party ? <Tag tone="amber">party</Tag> : null}
                  {p.ocr ? <Tag tone="rose">ocr</Tag> : null}
                  {p.pinned ? <Tag tone="sky">pinned</Tag> : null}
                  {p.expand ? <Tag>expanded</Tag> : null}
                </td>
              </tr>
              {openRow === i ? (
                <tr>
                  <td colSpan={9} className="bg-zinc-50 px-2 py-1 text-zinc-600">
                    <span className="font-mono text-zinc-400">{p.key}</span> {p.contract_id ? <Tag>{p.contract_id}</Tag> : null} {p.text || "No text preview."}
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CoverageMatrix({ rows }: { rows: TraceCoverageRow[] | undefined }) {
  if (!rows?.length) return <p className="text-xs text-zinc-500">Coverage not recorded for this question.</p>;
  return (
    <table className="w-full text-left text-xs text-zinc-700">
      <thead className="text-zinc-500">
        <tr>
          <th className="py-1 pr-2 font-medium">Subject</th>
          <th className="pr-2 font-medium">Kind</th>
          <th className="pr-2 font-medium">Asked</th>
          <th className="pr-2 font-medium">Passages</th>
          <th className="font-medium">Files</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={["border-t border-zinc-100 align-top", r.missing ? "bg-rose-50 text-rose-800" : ""].join(" ")}>
            <td className="py-1 pr-2 font-medium">{r.subject}</td>
            <td className="pr-2">{r.kind}</td>
            <td className="pr-2">{r.asked ?? ""}</td>
            <td className="pr-2">{r.passages}{r.missing ? " (missing)" : ""}</td>
            <td>{(r.files ?? []).join(", ")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function FactsSection({ trace }: { trace: QuestionTrace }) {
  const f = trace.facts;
  if (!f) return <NotRecorded />;
  const used = new Set(f.used_ids ?? []);
  const statusTone = (s?: string): Tone => (!s ? "zinc" : /verif|confirm|ok|approved/i.test(s) ? "emerald" : /conflict|reject|wrong|stale/i.test(s) ? "rose" : "amber");
  return (
    <div className="space-y-2 text-xs text-zinc-700">
      {f.offered?.length ? (
        <table className="w-full text-left">
          <thead className="text-zinc-500">
            <tr>
              <th className="py-1 pr-2 font-medium">Used</th>
              <th className="pr-2 font-medium">Contract</th>
              <th className="pr-2 font-medium">Fact</th>
              <th className="pr-2 font-medium">Value</th>
              <th className="pr-2 font-medium">Status</th>
              <th className="font-medium">Source</th>
            </tr>
          </thead>
          <tbody>
            {f.offered.map((o, i) => (
              <tr key={`${o.id}-${i}`} className="border-t border-zinc-100 align-top">
                <td className="py-1 pr-2">{used.has(o.id) ? <span className="text-emerald-700" aria-label="used in the answer">✓</span> : <span className="text-zinc-300">-</span>}</td>
                <td className="pr-2">{o.contract_id ?? ""}</td>
                <td className="pr-2">{[o.fact_type, o.subject].filter(Boolean).join(": ")}</td>
                <td className="pr-2">{o.value == null ? "" : String(o.value)}</td>
                <td className="pr-2">{o.status ? <Tag tone={statusTone(o.status)}>{o.status}</Tag> : null}</td>
                <td>
                  {o.file ?? ""}
                  {o.page != null ? ` p.${o.page}` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-zinc-500">No document facts were offered to the writer.</p>
      )}
      {f.registry?.length ? (
        <div>
          <p className="font-semibold">Registry rows</p>
          <ul className="list-disc pl-5 text-zinc-600">
            {f.registry.map((r, i) => (
              <li key={i}>
                <span className="font-medium">{r.kind}</span>: {r.detail}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {f.calculators?.length ? (
        <div>
          <p className="font-semibold">Calculators</p>
          <ul className="list-disc pl-5 text-zinc-600">
            {f.calculators.map((c, i) => (
              <li key={i}>
                <span className="font-medium">{c.name}</span>: {JSON.stringify(c.inputs ?? null)} gives {JSON.stringify(c.result ?? null)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function WriterSection({ trace }: { trace: QuestionTrace }) {
  const w = trace.writer;
  if (!w) return <NotRecorded />;
  const cells: [string, React.ReactNode][] = [
    ["Provider", w.provider],
    ["Model", w.model],
    ["Prompt size", w.prompt_chars != null ? `${w.prompt_chars.toLocaleString()} chars` : undefined],
    ["Passages in prompt", w.passages_in_prompt],
    ["Attempts", w.attempts],
    ["Fallback", w.fallback ? <Tag tone="amber">{w.fallback}</Tag> : w.fallback === null ? "none" : undefined],
    ["Shape", w.shape],
    ["Temperature", w.temperature],
    ["Seed", w.seed],
    ["HTTP statuses", w.statuses?.length ? w.statuses.join(", ") : undefined],
    ["Model used", w.used_model ? "yes" : `no${w.notice ? `: ${w.notice}` : ""}`],
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
      {cells.map(([k, v]) => (
        <div key={k}>
          <dt className="text-zinc-500">{k}</dt>
          <dd className="text-zinc-900">{v === undefined || v === null || v === "" ? <span className="text-zinc-400">not recorded</span> : v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function CitationsSection({ trace }: { trace: QuestionTrace }) {
  const c = trace.citations;
  if (!c) return <NotRecorded />;
  const list = (title: string, items: React.ReactNode[], tone = "text-zinc-600") =>
    items.length ? (
      <div>
        <p className="font-semibold text-zinc-800">{title} ({items.length})</p>
        <ul className={["list-disc pl-5", tone].join(" ")}>
          {items.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </div>
    ) : null;
  return (
    <div className="space-y-2 text-xs">
      {list("Cited", (c.cited ?? []).map((x) => `${x.key} ${x.file ?? ""}${x.page != null ? ` p.${x.page}` : ""}${x.where ? ` · ${x.where}` : ""}`))}
      {list("Dropped", (c.dropped ?? []).map((x) => `${x.key}: ${x.reason}`))}
      {list("Backfilled", (c.backfilled ?? []).map((x) => `${x.key} ${x.file ?? ""}${x.page != null ? ` p.${x.page}` : ""}`))}
      {list("Guard notes", (c.guard_notes ?? []).map((x) => `${x.claim}: ${x.reason}`), "text-amber-800")}
      {list("Inline keys stripped", (c.inline_keys_stripped ?? []).map(String))}
      {list("Cited keys not in the passages the writer saw", (c.not_in_passages ?? []).map(String), "text-rose-800")}
      {!(c.cited?.length || c.dropped?.length || c.backfilled?.length || c.guard_notes?.length || c.not_in_passages?.length) ? <p className="text-zinc-500">No citations recorded.</p> : null}
    </div>
  );
}

export function DroppedSection({ trace }: { trace: QuestionTrace }) {
  const d = trace.dropped;
  if (!d) return <NotRecorded />;
  const entries = Object.entries(d).filter(([, v]) => Array.isArray(v) && v.length) as [string, TracePassage[]][];
  if (!entries.length) return <p className="text-xs text-zinc-500">Nothing was dropped.</p>;
  return (
    <div className="space-y-2">
      {entries.map(([reason, items]) => (
        <div key={reason}>
          <p className="text-xs font-semibold text-zinc-800">
            {reason.replace("_", " ")} ({items.length})
          </p>
          <ChunksTable items={items} />
        </div>
      ))}
    </div>
  );
}

export function CacheSection({ trace }: { trace: QuestionTrace }) {
  const c = trace.cache;
  if (!c) return <NotRecorded />;
  return (
    <p className="text-xs text-zinc-700">
      <Tag tone={c.status === "hit" ? "sky" : "zinc"}>{c.status}</Tag>
      {c.key_prefix ? <span className="ml-2 font-mono text-zinc-500">key {c.key_prefix}</span> : null}
      {c.logic_version ? <span className="ml-2 text-zinc-500">logic {c.logic_version}</span> : null}
    </p>
  );
}

/** The new observability sections, each hidden-safe when its keys are missing. */
export function ObservabilitySections({ trace }: { trace: QuestionTrace }) {
  const chunks = trace.passages ?? [];
  const droppedCount = Object.values(trace.dropped ?? {}).reduce((s, v) => s + (Array.isArray(v) ? v.length : 0), 0);
  return (
    <div className="space-y-2">
      <Fold title="Chunks" count={trace.passages_count ?? chunks.length}>
        {trace.passages ? <ChunksTable items={chunks} /> : <NotRecorded />}
      </Fold>
      <Fold title="Fan-out: coverage" count={trace.coverage ? `${trace.coverage.filter((r) => r.missing).length} missing of ${trace.coverage.length}` : undefined}>
        <CoverageMatrix rows={trace.coverage} />
      </Fold>
      <Fold title="Facts" count={trace.facts?.offered?.length}>
        <FactsSection trace={trace} />
      </Fold>
      <Fold title="Writer">
        <WriterSection trace={trace} />
      </Fold>
      <Fold title="Citations" count={trace.citations?.cited?.length}>
        <CitationsSection trace={trace} />
      </Fold>
      <Fold title="Dropped passages by reason" count={trace.dropped ? droppedCount : undefined}>
        <DroppedSection trace={trace} />
      </Fold>
      <Fold title="Cache">
        <CacheSection trace={trace} />
      </Fold>
    </div>
  );
}
